import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button, Checkbox, Input, cn } from "@nexus/ui";
import { ApiError } from "@/lib/api-client";
import {
  applyBulkReplace,
  type BulkReplacePromptDto,
} from "@/features/prompts/api/prompts.api";
import { promptBulkPreviewQueryOptions, promptsKeys } from "@/lib/queries";
import { BulkReplaceConfirmDialog } from "./BulkReplaceConfirmDialog";

const SEARCH_DEBOUNCE_MS = 300;

function replaceFailureMessage(
  err: unknown,
  t: TFunction,
  fallbackKey: string,
  fallback: string,
): string {
  if (err instanceof ApiError && err.code === "TIMED_OUT") {
    return t("prompts.replace.timedOut", "That pattern took too long. Try a simpler one.");
  }
  if (err instanceof ApiError && err.code === "BUSY") {
    return t("prompts.replace.busy", "Too many replacements are running. Try again.");
  }
  if (err instanceof ApiError && err.code === "CANCELLED") {
    return t("prompts.replace.cancelled", "That replacement was cancelled.");
  }
  if (err instanceof Error && err.message) return err.message;
  return t(fallbackKey, fallback);
}

interface PromptSearchReplacePanelProps {
  initialFind?: string;
  onOpenMatch: (promptId: string, line: number) => void;
  onClose: () => void;
}

function selectableMatchIds(prompt: BulkReplacePromptDto): string[] {
  return prompt.matches
    .filter((match) => (match.undefinedVariables ?? []).length === 0)
    .map((match) => match.id);
}

function useDebouncedValue(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

export function PromptSearchReplacePanel({
  initialFind = "",
  onOpenMatch,
  onClose,
}: PromptSearchReplacePanelProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [find, setFind] = useState(initialFind);
  const [replace, setReplace] = useState("");
  const [matchCase, setMatchCase] = useState(false);
  const [wholeWord, setWholeWord] = useState(false);
  const [regex, setRegex] = useState(false);
  const [checked, setChecked] = useState<Record<string, string[]>>({});
  const [confirmOpen, setConfirmOpen] = useState(false);
  const debouncedFind = useDebouncedValue(find, SEARCH_DEBOUNCE_MS);
  const debouncedReplace = useDebouncedValue(replace, SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    setFind(initialFind);
  }, [initialFind]);

  const preview = useQuery({
    ...promptBulkPreviewQueryOptions({
      find: debouncedFind,
      replace: debouncedReplace,
      matchCase,
      wholeWord,
      regex,
    }),
    meta: { skipGlobalError: true },
  });

  const resultKey = useMemo(
    () =>
      (preview.data ?? [])
        .map((prompt) =>
          `${prompt.promptId}:${prompt.matches
            .map((match) => `${match.id}${(match.undefinedVariables ?? []).length > 0 ? "!" : ""}`)
            .join(",")}`,
        )
        .join("|"),
    [preview.data],
  );

  useEffect(() => {
    const next: Record<string, string[]> = {};
    for (const prompt of preview.data ?? []) {
      next[prompt.promptId] = selectableMatchIds(prompt);
    }
    setChecked(next);
  }, [resultKey]);

  const selectedPrompts = useMemo(() => {
    return (preview.data ?? []).filter((prompt) => (checked[prompt.promptId] ?? []).length > 0);
  }, [preview.data, checked]);

  const applyMutation = useMutation({
    mutationFn: () =>
      applyBulkReplace({
        find: debouncedFind,
        replace: debouncedReplace,
        matchCase,
        wholeWord,
        regex,
        selections: selectedPrompts.map((prompt) => ({
          promptId: prompt.promptId,
          expectedVersionId: prompt.versionId,
          matchIds: checked[prompt.promptId] ?? [],
        })),
      }),
    onSuccess: () => {
      toast.success(
        t("prompts.replace.applied", "Replaced in {{count}} prompts", {
          count: selectedPrompts.length,
        }),
      );
      setConfirmOpen(false);
      void queryClient.invalidateQueries({ queryKey: promptsKeys.all });
      void preview.refetch();
    },
    onError: (err: Error) => {
      toast.error(replaceFailureMessage(err, t, "prompts.replace.applyFailed", "Replace failed"));
    },
    meta: { skipGlobalError: true },
  });

  const togglePrompt = (prompt: BulkReplacePromptDto, on: boolean) => {
    setChecked((current) => ({
      ...current,
      [prompt.promptId]: on ? selectableMatchIds(prompt) : [],
    }));
  };

  const toggleMatch = (promptId: string, matchId: string, on: boolean) => {
    setChecked((current) => {
      const existing = new Set(current[promptId] ?? []);
      if (on) existing.add(matchId);
      else existing.delete(matchId);
      return { ...current, [promptId]: [...existing] };
    });
  };

  return (
    <aside
      aria-label={t("prompts.replace.panelAria", "Search and replace")}
      className="flex h-full w-[380px] shrink-0 flex-col border-l border-border bg-card"
    >
      <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
        <h2 className="text-[13px] font-medium">
          {t("prompts.replace.title", "Search & replace")}
        </h2>
        <Button type="button" variant="ghost" size="sm" onClick={onClose}>
          {t("common.close", "Close")}
        </Button>
      </div>

      <div className="flex flex-col gap-2 border-b border-border px-3 py-3">
        <Input
          value={find}
          onChange={(event) => setFind(event.target.value)}
          aria-label={t("prompts.replace.findAria", "Find")}
          placeholder={t("prompts.replace.find", "Find")}
          className="h-8 text-[13px]"
        />
        <Input
          value={replace}
          onChange={(event) => setReplace(event.target.value)}
          aria-label={t("prompts.replace.replaceAria", "Replace")}
          placeholder={t("prompts.replace.replace", "Replace")}
          className="h-8 text-[13px]"
        />
        <div className="flex flex-wrap gap-1">
          <ToggleChip
            pressed={matchCase}
            onClick={() => setMatchCase((value) => !value)}
            label={t("prompts.replace.matchCase", "Match case")}
          />
          <ToggleChip
            pressed={wholeWord}
            onClick={() => setWholeWord((value) => !value)}
            label={t("prompts.replace.wholeWord", "Whole word")}
          />
          <ToggleChip
            pressed={regex}
            onClick={() => setRegex((value) => !value)}
            label={t("prompts.replace.regex", "Regex")}
          />
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-2">
        {preview.isError ? (
          <p role="alert" className="text-[12px] text-destructive">
            {replaceFailureMessage(
              preview.error,
              t,
              "prompts.replace.searchFailed",
              "Search failed",
            )}
          </p>
        ) : null}
        {debouncedFind.trim() && preview.data && preview.data.length === 0 ? (
          <p className="text-[12px] text-muted-foreground">
            {t("prompts.replace.noMatches", "No matches")}
          </p>
        ) : null}
        <ul className="space-y-3">
          {(preview.data ?? []).map((prompt) => {
            const selected = checked[prompt.promptId] ?? [];
            const selectable = selectableMatchIds(prompt);
            const allSelectableSelected =
              selectable.length > 0 && selectable.every((id) => selected.includes(id));
            const promptState = allSelectableSelected
              ? true
              : selected.length === 0
                ? false
                : "indeterminate";
            return (
              <li key={prompt.promptId} className="space-y-1">
                <label className="flex items-center gap-2 text-[13px] font-medium">
                  <Checkbox
                    checked={promptState}
                    onCheckedChange={(value) => togglePrompt(prompt, value === true)}
                    aria-label={t("prompts.replace.selectPrompt", "Select {{name}}", {
                      name: prompt.name,
                    })}
                  />
                  <span className="min-w-0 truncate">{prompt.name}</span>
                  {prompt.isGlobal ? (
                    <span className="shrink-0 text-[11px] font-normal text-warning-subtle-foreground">
                      {t("prompts.table.global", "Global")}
                    </span>
                  ) : null}
                </label>
                <ul className="space-y-1 pl-6">
                  {prompt.matches.map((match) => {
                    const isFlagged = (match.undefinedVariables ?? []).length > 0;
                    return (
                      <li key={match.id} className="flex items-start gap-2">
                        <Checkbox
                          className="mt-0.5"
                          checked={selected.includes(match.id)}
                          disabled={isFlagged}
                          onCheckedChange={(value) =>
                            toggleMatch(prompt.promptId, match.id, value === true)
                          }
                          aria-label={t("prompts.replace.selectMatch", "Select match on line {{line}}", {
                            line: match.line,
                          })}
                        />
                        <button
                          type="button"
                          className="min-w-0 flex-1 cursor-pointer rounded-sm text-left text-[12px] focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
                          onClick={() => onOpenMatch(prompt.promptId, match.line)}
                        >
                          <span className="text-muted-foreground">{match.line}: </span>
                          <span className="line-through">{match.before}</span>{" "}
                          <span className="text-emerald-700 dark:text-emerald-400">{match.after}</span>
                        </button>
                        {isFlagged ? (
                          <span className="shrink-0 text-[11px] text-destructive">
                            {t("prompts.replace.undefinedVariables", "Undefined: {{names}}", {
                              names: (match.undefinedVariables ?? []).join(", "),
                            })}
                          </span>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="flex items-center justify-between gap-2 border-t border-border px-3 py-2">
        <p className="text-[11px] text-muted-foreground">
          {t("prompts.replace.selectedCount", "{{count}} selected", {
            count: selectedPrompts.length,
          })}
        </p>
        <Button
          type="button"
          size="sm"
          disabled={selectedPrompts.length === 0 || !debouncedFind.trim()}
          onClick={() => setConfirmOpen(true)}
        >
          {t("prompts.replace.apply", "Apply")}
        </Button>
      </div>

      <BulkReplaceConfirmDialog
        open={confirmOpen}
        prompts={selectedPrompts}
        isPending={applyMutation.isPending}
        onOpenChange={setConfirmOpen}
        onConfirm={() => applyMutation.mutate()}
      />
    </aside>
  );
}

function ToggleChip({
  pressed,
  onClick,
  label,
}: {
  pressed: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      className={cn(
        "h-7 cursor-pointer rounded-md border px-2 text-[11px]",
        pressed
          ? "border-primary bg-primary text-primary-foreground"
          : "border-border bg-card text-foreground",
      )}
    >
      {label}
    </button>
  );
}
